/* eslint-disable @typescript-eslint/prefer-promise-reject-errors */
/* eslint-disable @typescript-eslint/no-unsafe-argument */

/* eslint-disable @typescript-eslint/no-unsafe-member-access */

import React, { useMemo } from "react";

import { message } from "antd";
import { useClient } from "urql";

import type { Insurer } from "@alaffia-technology-solutions/client-sdk";

import {
  InsurersOrderBy,
  InsurerUsersOrderBy,
} from "@alaffia-technology-solutions/client-sdk";
import { apiClient } from "@alaffia-technology-solutions/client-sdk-react-query";

import type { ClaimIntakeUpload } from "./util";

import { UserTypeEnum } from "../../gql/graphql";
import { useUserContext } from "../context/User.context";
import { UploadClaimModalUi } from "./uploadClaimModalUi";
import { uploadFile } from "./util";

export const UploadClaimModal = ({
  open,
  setOpen,
  setUploadClaimPending,
}: {
  open: boolean;
  setOpen: (b: boolean) => void;
  setUploadClaimPending: (i: number) => void;
}) => {
  const { id, userType } = useUserContext();

  const graphqlClient = useClient();

  const [loading, setLoading] = React.useState(false);
  const [uploadList, setUploadList] = React.useState<ClaimIntakeUpload[]>([]);
  const [history, setHistory] = React.useState<ClaimIntakeUpload[]>([]);
  const [insurerId, setInsurerId] = React.useState<string | null>(null);
  const [claimNumber, setClaimNumber] = React.useState<string | null>(null);

  /**
   * copy the completed (by default) transactions to the history list and prune the current session list
   * @param clearCurrent -- resets anything that would have been kept (e.g. reset()); default true
   * @param excludeStatuses  -- array of upload.metadata.statuses not to move to history; default ['ready']
   */
  const copyToHistory = (
    clearCurrent = true,
    excludeStatuses = ["ready"],
    callback?: (uploadListUpdated: {
      addToHistory: ClaimIntakeUpload[];
      keepInCurrent: ClaimIntakeUpload[];
    }) => void,
  ) => {
    setUploadList((batchSafeUploadList) => {
      const { addToHistory, keepInCurrent } = batchSafeUploadList.reduce<{
        addToHistory: ClaimIntakeUpload[];
        keepInCurrent: ClaimIntakeUpload[];
      }>(
        (acc, upload) => {
          if (excludeStatuses.includes(upload?.metadata?.status ?? "")) {
            if (!clearCurrent) {
              acc.keepInCurrent.push(upload);
            }
          } else {
            acc.addToHistory.push(upload);
          }
          return acc;
        },
        { addToHistory: [], keepInCurrent: [] },
      );
      setHistory((batchSafeHistory) => [...batchSafeHistory, ...addToHistory]);
      // and return any leftovers as replacement for the current list:
      if (callback && typeof callback === "function") {
        callback({
          addToHistory,
          keepInCurrent,
        });
      }
      return [...keepInCurrent];
    });
  };

  const [uploadInProgress, setUploadInProgress] = React.useState(false);

  const isAdmin = userType === UserTypeEnum.ADMINISTRATOR;

  const { data: adminData, isPending: isAdminPending } =
    apiClient.insurers.getInsurers.useQuery(
      {
        orderBy: InsurersOrderBy.NAME_ASC,
      },
      {
        enabled: isAdmin,
      },
    );

  const { data, isPending } = apiClient.auditorUsers.getAuditorUser.useQuery(
    {
      id,
      insurerUsersOrderBy: InsurerUsersOrderBy.INSURER_BY_INSURER_ID__NAME_ASC,
    },
    {
      enabled: !isAdmin,
    },
  );

  const fetching = isAdmin ? isAdminPending : isPending;

  const insurers: Insurer[] = fetching
    ? []
    : isAdmin
      ? (adminData?.nodes ?? []).flatMap((insurer: Insurer | null) =>
          insurer ? [insurer] : [],
        )
      : (data?.insurerUsers?.nodes ?? []).flatMap(
          (insUser: { insurer?: Insurer | null } | null) =>
            insUser?.insurer ? [insUser.insurer] : [],
        );

  const insurerName = useMemo(
    () => insurers.find((ins) => ins.id === insurerId)?.name ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [insurerId],
  );

  const isSameUpload = (
    upload1: ClaimIntakeUpload,
    upload2: ClaimIntakeUpload,
  ) => upload1.uploadFile.uid === upload2.uploadFile.uid;

  const uploadFilesHandler = async () => {
    if (uploadList.length === 0) return;

    setLoading(true);
    setUploadInProgress(true);

    // filter to list to upload/retry by status
    const toUploadFilter = (upload: ClaimIntakeUpload) =>
      upload.metadata.status !== "success";

    // Show individual file uploading
    setUploadList((batchSafeUploadList) =>
      batchSafeUploadList.map((f) => {
        return toUploadFilter(f)
          ? { ...f, metadata: { ...f.metadata, status: "pending" } }
          : { ...f };
      }),
    );

    // Wait for all of the uploads to resolve/reject
    const completed = Promise.allSettled(
      // Filter out uploads that have already succeeded
      uploadList.filter(toUploadFilter).map(async (upload) =>
        uploadFile(upload, graphqlClient)
          .then((successUpload) => {
            addOrUpdate(successUpload);
            return Promise.resolve(successUpload);
          })
          .catch((errorUpload) => {
            console.log(
              `Upload failed for ${errorUpload.uploadFile.name}${
                errorUpload.error ? ` -- ${errorUpload.error}` : ""
              }`,
              errorUpload,
            );
            addOrUpdate(errorUpload);
            return Promise.reject(errorUpload);
          }),
      ),
    ).then(() => {
      setLoading(false);
      setUploadInProgress(false);
    });
    return completed;
  };

  const setUploadKind = (updatedUpload: ClaimIntakeUpload, kind: string) => {
    setUploadList((batchSafeUploadList) => {
      const newList = batchSafeUploadList.map((existingUpload) => {
        if (isSameUpload(existingUpload, updatedUpload)) {
          return {
            ...existingUpload,
            metadata: {
              ...existingUpload.metadata,
              documentUploadKind: kind,
            },
          };
        }
        return existingUpload;
      });

      return markDuplicates(newList);
    });
  };

  const isDuplicateNameAndKind = (
    upload: ClaimIntakeUpload,
    otherUploads: ClaimIntakeUpload[],
  ) =>
    Boolean(upload.metadata.documentUploadKind) &&
    otherUploads.filter(
      (otherUpload) =>
        otherUpload.uploadFile.name === upload.uploadFile.name &&
        otherUpload.metadata.documentUploadKind ===
          upload.metadata.documentUploadKind &&
        otherUpload.uploadFile.uid !== upload.uploadFile.uid,
    ).length > 0;

  const isDuplicateUB = (
    upload: ClaimIntakeUpload,
    otherUploads: ClaimIntakeUpload[],
  ) =>
    upload.metadata.documentUploadKind !== "UB04_CLAIM_FORM"
      ? false
      : otherUploads.filter(
          (otherUpload) =>
            otherUpload.metadata.documentUploadKind === "UB04_CLAIM_FORM" &&
            otherUpload.uploadFile.uid !== upload.uploadFile.uid,
        ).length > 0;

  const markDuplicates = (
    newUploadList: ClaimIntakeUpload[],
  ): ClaimIntakeUpload[] =>
    newUploadList.map((upload) => {
      const duplicateNameAndKind = isDuplicateNameAndKind(
        upload,
        newUploadList,
      );
      const duplicateUB = isDuplicateUB(upload, newUploadList);
      if (
        duplicateNameAndKind !== upload.metadata.duplicateNameAndKind ||
        duplicateUB !== upload.metadata.duplicateUB
      ) {
        return {
          ...upload,
          uploadFile: upload.uploadFile,
          metadata: { ...upload.metadata, duplicateNameAndKind, duplicateUB },
        };
      }
      return upload;
    });

  const addOrUpdate = (newUpload: ClaimIntakeUpload) => {
    const fileSize = newUpload.uploadFile.size ?? 0;

    if (fileSize <= 0) {
      message.error(`Cannot upload empty file: ${newUpload.uploadFile.name}`);
      return;
    }

    setUploadList((batchSafeUploadList) => {
      let found = false;
      const newList = [
        ...batchSafeUploadList.map((existingUpload) => {
          if (isSameUpload(existingUpload, newUpload)) {
            found = true;
            return {
              ...existingUpload,
              metadata: { ...existingUpload.metadata, ...newUpload.metadata },
              uploadFile: newUpload.uploadFile, // N.B. we do NOT spread since it doesn't have many 'own' properties
            };
          }
          return existingUpload;
        }),
      ];
      if (!found) {
        newList.push(newUpload);
      }

      // mark dupes in new list and return
      return markDuplicates(newList);
    });
  };

  const removeUpload = (uploadToRemove: ClaimIntakeUpload) => {
    setUploadList((batchSafeUploadList) => {
      const existingEntry =
        batchSafeUploadList.find((existingUpload) =>
          isSameUpload(existingUpload, uploadToRemove),
        ) ?? null;

      if (existingEntry?.metadata.status === "pending") {
        console.log(
          "Skipping remove for in-progress upload",
          uploadToRemove.uploadFile.name,
        );
        return batchSafeUploadList;
      }

      // merge with any metadata updates that may have happened but not rendered yet:
      const mergedEntryToRemove = existingEntry
        ? {
            ...existingEntry,
            metadata: {
              ...existingEntry.metadata,
              ...uploadToRemove.metadata,
            },
          }
        : uploadToRemove;

      const newList = [
        ...batchSafeUploadList.filter(
          (existingUpload) => !isSameUpload(existingUpload, uploadToRemove),
        ),
      ];

      if (existingEntry && mergedEntryToRemove.metadata.status !== "ready") {
        setHistory((batchSafeHistory) => [
          ...batchSafeHistory,
          mergedEntryToRemove,
        ]);
      }
      return markDuplicates(newList);
    });
  };

  const addFileToUploadList = (newUpload: ClaimIntakeUpload) => {
    addOrUpdate(newUpload);
    return false;
  };

  const reset = () => copyToHistory();

  const close = () => {
    copyToHistory(false, ["ready", "pending", "error"], (updated) => {
      setUploadClaimPending(updated.keepInCurrent.length);
      if (updated.keepInCurrent.length > 0) {
        void message.warning(
          "Some Claim Upload files were not uploaded, reopen the dialog to see pending uploads",
        );
      }
      setOpen(false);
    });
  };

  const props = {
    open,
    loading,
    uploadList,
    history,
    uploadInProgress,
    insurers,
    insurerName,
    insurerId,
    setInsurerId,
    claimNumber,
    setClaimNumber,
    uploadFilesHandler,
    setUploadKind,
    addFileToUploadList,
    addOrUpdate,
    removeUpload,
    reset,
    close,
  };

  return <UploadClaimModalUi {...props}></UploadClaimModalUi>;
};
